// First-touch traffic attribution: remember how a visitor first reached the site so
// the signup — and the order that follows — can be tied back to a source.
//
// Deliberately not a third-party analytics script: this is a few lines in a bundle we
// already ship, so it costs no extra page weight and sends nothing to anyone else.
// Same shape as utils/referral.js (capture at landing -> localStorage -> attach to signup).
//
// FIRST touch wins: someone who arrives from a Google search, leaves, and comes back
// directly a week later is still credited to search. Overwriting on the last visit
// would credit "direct" for traffic search actually earned.

export const ATTR_KEY = 'se_attr';
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days — CE is a considered purchase tied to renewal dates

const SEARCH_ENGINES = ['google', 'bing', 'duckduckgo', 'yahoo', 'ecosia', 'brave', 'startpage'];
const SOCIAL = ['facebook', 'instagram', 'linkedin', 'twitter', 'x.com', 't.co', 'reddit', 'youtube'];

const clean = (v, max = 100) =>
  (v == null ? '' : String(v)).trim().toLowerCase().replace(/[^a-z0-9._\-/? =+%]/g, '').slice(0, max);

// Reduce a referrer hostname to something readable: "www.google.co.uk" -> "google".
// Multi-part suffixes (.co.uk, .com.au) need the extra hop, otherwise the second-to-last
// label is the suffix itself and every UK search shows up as a site called "co".
const SUFFIX_LABELS = ['co', 'com', 'org', 'net', 'ac', 'gov', 'edu'];
function hostRoot(host) {
  const parts = host.replace(/^www\./, '').split('.');
  if (parts.length <= 2) return parts[0];
  const idx = SUFFIX_LABELS.includes(parts[parts.length - 2]) ? parts.length - 3 : parts.length - 2;
  return parts[Math.max(0, idx)];
}

function classify(search, referrer, ownHost) {
  const qs = new URLSearchParams(search || '');
  const utmSource = clean(qs.get('utm_source'), 60);
  const utmMedium = clean(qs.get('utm_medium'), 60);
  const utmCampaign = clean(qs.get('utm_campaign'), 80);

  // An explicit UTM always wins — it's what the campaign said about itself.
  if (utmSource) {
    return { source: utmSource, medium: utmMedium || 'unknown', campaign: utmCampaign || '' };
  }

  // A friend-referral link is its own channel, not a generic referral.
  if (qs.get('ref')) {
    return { source: 'friend-referral', medium: 'referral', campaign: '' };
  }

  if (!referrer) return { source: 'direct', medium: 'none', campaign: '' };

  let host = '';
  try { host = new URL(referrer).hostname.toLowerCase(); } catch { return { source: 'direct', medium: 'none', campaign: '' }; }

  // Internal navigation is not a new source.
  if (!host || host === ownHost || host.endsWith(`.${ownHost}`)) {
    return { source: 'direct', medium: 'none', campaign: '' };
  }

  const root = hostRoot(host);
  if (SEARCH_ENGINES.includes(root)) return { source: root, medium: 'organic', campaign: '' };
  if (SOCIAL.some(s => host.includes(s))) return { source: root, medium: 'social', campaign: '' };
  return { source: root, medium: 'referral', campaign: '' };
}

// Record the first landing only. Safe to call on every route change; it no-ops once set.
export function captureAttribution(search, pathname) {
  try {
    if (typeof window === 'undefined') return;
    const existing = readRaw();
    if (existing) return; // first touch already recorded

    const referrer = typeof document !== 'undefined' ? document.referrer || '' : '';
    const ownHost = window.location.hostname.replace(/^www\./, '');
    const { source, medium, campaign } = classify(
      search != null ? search : window.location.search,
      referrer,
      ownHost
    );

    localStorage.setItem(ATTR_KEY, JSON.stringify({
      source,
      medium,
      campaign,
      landing: clean(pathname != null ? pathname : window.location.pathname, 200),
      referrer: clean(referrer, 200),
      ts: Date.now(),
    }));
  } catch {}
}

function readRaw() {
  try {
    const v = JSON.parse(localStorage.getItem(ATTR_KEY));
    if (!v || !v.source) return null;
    if (Date.now() - v.ts > TTL_MS) { localStorage.removeItem(ATTR_KEY); return null; }
    return v;
  } catch { return null; }
}

// The payload attached to a signup. Null when nothing was captured.
export function getStoredAttribution() {
  const v = readRaw();
  if (!v) return null;
  return {
    source: v.source,
    medium: v.medium,
    campaign: v.campaign || '',
    landing: v.landing || '',
    referrer: v.referrer || '',
  };
}
